import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import "../components/css/BlobCursor.css";

const BlobCursor: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const dotRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const isDarkRef = useRef(false);

  // useMotionValue + useSpring: position updates never trigger React re-renders
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);
  const springX = useSpring(mouseX, { stiffness: 80, damping: 20, mass: 0.8 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 20, mass: 0.8 });

  const applyColors = (dark: boolean) => {
    const fg = dark ? 'white' : 'black';
    const bg = dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
    if (dotRef.current) dotRef.current.style.backgroundColor = fg;
    if (blob1Ref.current) {
      blob1Ref.current.style.borderColor = fg;
      blob1Ref.current.style.backgroundColor = bg;
    }
    if (blob2Ref.current) blob2Ref.current.style.borderColor = fg;
  };

  useEffect(() => {
    document.documentElement.style.cursor = 'none';
    document.body.style.cursor = 'none';

    const interactive = document.querySelectorAll('a, button, input, [role="button"]');
    interactive.forEach(el => { (el as HTMLElement).style.cursor = 'none'; });

    const getEffectiveBackground = (el: Element | null): { r: number; g: number; b: number } | null => {
      let current = el;
      while (current && current !== document.documentElement) {
        const bg = window.getComputedStyle(current).backgroundColor;
        const match = bg.match(/[\d.]+/g);
        if (match && match.length >= 3) {
          const r = Number(match[0]);
          const g = Number(match[1]);
          const b = Number(match[2]);
          const a = match[4] !== undefined ? Number(match[4]) : (match[3] !== undefined ? Number(match[3]) : 1);
          if (a > 0.1) return { r, g, b };
        }
        current = current.parentElement;
      }
      return null;
    };

    const move = (e: MouseEvent) => {
      // Move dot directly via transform — no React re-render
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX - 5}px, ${e.clientY - 5}px)`;
      }

      // Move blob via motion values — no React re-render
      mouseX.set(e.clientX - 25);
      mouseY.set(e.clientY - 25);

      // Detect background darkness
      const els = document.elementsFromPoint(e.clientX, e.clientY);
      let dark = false;
      for (const el of els) {
        if (el === document.documentElement || el === document.body) continue;
        const color = getEffectiveBackground(el);
        if (color) {
          const luminance = 0.299 * color.r + 0.587 * color.g + 0.114 * color.b;
          dark = luminance < 80;
          break;
        }
      }

      // Only update DOM if color actually changed
      if (dark !== isDarkRef.current) {
        isDarkRef.current = dark;
        applyColors(dark);
      }
    };

    const handleMouseOver = (e: MouseEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'A' || tag === 'BUTTON') setIsHovered(true);
    };
    const handleMouseOut = () => setIsHovered(false);

    window.addEventListener("mousemove", move);
    document.addEventListener("mouseover", handleMouseOver);
    document.addEventListener("mouseout", handleMouseOut);

    return () => {
      document.documentElement.style.cursor = 'auto';
      document.body.style.cursor = 'auto';
      interactive.forEach(el => { (el as HTMLElement).style.cursor = 'auto'; });
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", handleMouseOver);
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, [mouseX, mouseY]);

  const blobVariants = {
    default: {
      borderRadius: [
        "35% 65% 65% 35% / 35% 30% 70% 65%",
        "65% 35% 30% 70% / 65% 65% 35% 35%",
        "35% 65% 65% 35% / 65% 35% 65% 35%",
        "65% 35% 35% 65% / 35% 65% 35% 65%"
      ],
      rotate: [0, 90, 180, 270, 360],
      transition: {
        duration: 20,
        repeat: Infinity,
        ease: [0.4, 0, 0.6, 1] as any,
        times: [0, 0.25, 0.5, 0.75, 1]
      }
    },
    hover: {
      borderRadius: [
        "30% 70% 70% 30% / 30% 30% 70% 70%",
        "70% 30% 30% 70% / 70% 70% 30% 30%",
        "30% 70% 70% 30% / 70% 30% 70% 30%",
        "70% 30% 30% 70% / 30% 70% 30% 70%"
      ],
      scale: 1.2,
      rotate: [0, 90, 180, 270, 360],
      transition: {
        duration: 15,
        repeat: Infinity,
        ease: [0.4, 0, 0.6, 1] as any,
        times: [0, 0.25, 0.5, 0.75, 1]
      }
    }
  };

  return (
    <>
      {/* Cursor dot — positioned via direct DOM transform, no state */}
      <div
        ref={dotRef}
        style={{
          width: '10px',
          height: '10px',
          backgroundColor: 'black',
          borderRadius: '50%',
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: 9999,
          top: 0,
          left: 0,
          willChange: 'transform',
        }}
      />

      {/* Blob — uses motion values for spring, no re-render on position change */}
      <motion.div
        className="pointer-events-none fixed top-0 left-0"
        style={{ zIndex: 9998, x: springX, y: springY }}
      >
        <motion.div
          ref={blob1Ref}
          style={{
            width: '50px',
            height: '50px',
            border: '1px solid black',
            backgroundColor: 'rgba(0,0,0,0.1)',
          }}
          initial="default"
          animate={isHovered ? "hover" : "default"}
          variants={blobVariants}
        />
        <motion.div
          ref={blob2Ref}
          style={{
            width: '50px',
            height: '50px',
            border: '1px solid black',
            backgroundColor: 'transparent',
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0.5,
          }}
          initial="default"
          animate={isHovered ? "hover" : "default"}
          variants={blobVariants}
          transition={{
            type: "spring",
            stiffness: 50,
            damping: 30,
            mass: 1.5,
            delay: 0.1
          }}
        />
      </motion.div>
    </>
  );
};

export default BlobCursor;
